'use client'

import { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import { getReactions, toggleReaction, type ReactionCounts, type ReactionType } from '@/lib/actions/reactions'
import { cn } from '@/lib/utils'

interface ReactionBarProps {
    entityType: string
    entityId: string
}

export function ReactionBar({ entityType, entityId }: ReactionBarProps) {
    const [counts, setCounts] = useState<ReactionCounts>({ heart: 0, clap: 0, laugh: 0, wow: 0 })
    const [myReaction, setMyReaction] = useState<ReactionType | null>(null)
    const [loading, setLoading] = useState(true)
    const [visitorId, setVisitorId] = useState<string>('')

    useEffect(() => {
        // 1. Get or Create Visitor ID
        let vid = Cookies.get('daalu_visitor_id')
        if (!vid) {
            vid = crypto.randomUUID()
            Cookies.set('daalu_visitor_id', vid, { expires: 365 })
        }
        setVisitorId(vid)

        // 2. Fetch initial data
        getReactions(entityType, entityId, vid)
            .then(data => {
                setCounts(data.counts)
                setMyReaction(data.userReaction)
                setLoading(false)
            })
            .catch(err => console.error(err))
    }, [entityType, entityId])

    const handleReaction = async (type: ReactionType) => {
        if (!visitorId) return

        // Mute optimistic update
        const previousReaction = myReaction
        const previousCounts = { ...counts }

        // Determine new state
        let newReaction: ReactionType | null = type
        const newCounts = { ...counts }

        if (previousReaction === type) {
            // Toggle OFF
            newReaction = null
            newCounts[type] = Math.max(0, newCounts[type] - 1)
        } else {
            // Toggle ON (or Switch)
            if (previousReaction) {
                newCounts[previousReaction] = Math.max(0, newCounts[previousReaction] - 1)
            }
            newCounts[type]++
        }

        // Apply Optimistic
        setMyReaction(newReaction)
        setCounts(newCounts)

        // Call Server
        try {
            await toggleReaction(entityType, entityId, type, visitorId, '/actualites/[id]') // Revalidate path is generic, might need to be specific or handled better
        } catch (error) {
            // Revert on error
            setMyReaction(previousReaction)
            setCounts(previousCounts)
            console.error("Failed to react", error)
        }
    }

    if (loading) {
        return <div className="h-12 w-full animate-pulse bg-white/5 rounded-full" />
    }

    const emojis: { type: ReactionType; icon: string; label: string }[] = [
        { type: 'heart', icon: '❤️', label: 'J\'aime' },
        { type: 'clap', icon: '👏', label: 'Bravo' },
        { type: 'laugh', icon: '😂', label: 'Haha' },
        { type: 'wow', icon: '😮', label: 'Waouh' },
    ]

    return (
        <div className="flex flex-wrap items-center gap-2 select-none">
            {emojis.map((emoji) => {
                const isActive = myReaction === emoji.type
                return (
                    <button
                        key={emoji.type}
                        onClick={() => handleReaction(emoji.type)}
                        className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-200 border",
                            isActive
                                ? "bg-primary/20 border-primary text-white scale-105"
                                : "bg-white/5 border-transparent text-gray-400 hover:bg-white/10 hover:scale-110"
                        )}
                    >
                        <span className="text-xl">{emoji.icon}</span>
                        <span className={cn("text-xs font-bold", isActive ? "text-primary" : "text-gray-500")}>
                            {counts[emoji.type] > 0 ? counts[emoji.type] : ''}
                        </span>
                    </button>
                )
            })}
        </div>
    )
}
